import type { ElementNode } from "../types";

/** Default free animation JSON on Lordicon’s CDN (editable in the inspector). */
export const DEFAULT_LORDICON_JSON_SRC =
	"https://cdn.lordicon.com/wxnxiano.json";

const ALLOWED_TRIGGERS = new Set([
	"hover",
	"click",
	"loop",
	"loop-on-hover",
	"morph",
	"boom-line",
	"in-screen",
	"sequence",
]);

export function sanitizeLordiconTrigger(raw: string | undefined): string {
	const t = (raw ?? "hover").trim().toLowerCase();
	return ALLOWED_TRIGGERS.has(t) ? t : "hover";
}

/**
 * Allowed animation sources:
 * - Stable CDN: `https://cdn.lordicon.com/<slug>.json`
 * - Time-limited API links from Lordicon (`api.lordicon.com/….json`) after picking from the library.
 */
export function sanitizeLordiconSrc(url: string | undefined): string | null {
	const s = url?.trim() ?? "";
	if (!s) {
		return null;
	}
	let u: URL;
	try {
		u = new URL(s);
	} catch {
		return null;
	}
	if (u.protocol !== "https:") {
		return null;
	}
	if (u.hostname === "cdn.lordicon.com") {
		if (!/^\/[a-z0-9_-]+\.json$/i.test(u.pathname)) {
			return null;
		}
		return u.toString();
	}
	if (u.hostname === "api.lordicon.com") {
		if (!u.pathname.toLowerCase().endsWith(".json")) {
			return null;
		}
		return u.toString();
	}
	return null;
}

function normalizeHex6(raw: string | undefined): string | undefined {
	const s = raw?.trim() ?? "";
	if (!s) {
		return undefined;
	}
	if (/^#[0-9a-f]{6}$/i.test(s)) {
		return s.toLowerCase();
	}
	const short = /^#([0-9a-f]{3})$/i.exec(s);
	if (short?.[1]) {
		const x = short[1];
		return `#${x[0]}${x[0]}${x[1]}${x[1]}${x[2]}${x[2]}`.toLowerCase();
	}
	return undefined;
}

/** Lordicon `colors` attribute: `primary:#hex,secondary:#hex`. */
export function formatLordiconColors(
	primary?: string,
	secondary?: string
): string | undefined {
	const parts: string[] = [];
	const p = normalizeHex6(primary);
	const s = normalizeHex6(secondary);
	if (p) {
		parts.push(`primary:${p}`);
	}
	if (s) {
		parts.push(`secondary:${s}`);
	}
	return parts.length > 0 ? parts.join(",") : undefined;
}

export function builderDocContainsLordicon(
	nodes: ElementNode[] | undefined
): boolean {
	if (!nodes?.length) {
		return false;
	}
	for (const n of nodes) {
		if (n.type === "lordicon_icon") {
			return true;
		}
		if (
			"children" in n &&
			Array.isArray(n.children) &&
			builderDocContainsLordicon(n.children)
		) {
			return true;
		}
	}
	return false;
}
